import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import api from "../services/api";
import {
  Assessment,
  Add,
  Edit,
  Visibility,
  CheckCircle,
  Pending,
  Star,
} from "@mui/icons-material";

export default function PerformanceManagement() {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchEvaluations();
  }, [filter]);

  const fetchEvaluations = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== "all") params.status = filter;
      const response = await api.get("/performance/evaluations", { params });
      setEvaluations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to fetch evaluations", err);
      toast.error("Failed to load performance evaluations");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: "bg-gray-100 text-gray-700",
      self_assessment_pending: "bg-blue-100 text-blue-700",
      manager_review_pending: "bg-orange-100 text-orange-700",
      completed: "bg-green-100 text-green-700",
    };
    return badges[status] || badges.draft;
  };

  const getStatusIcon = (status) => {
    if (status === "completed") return <CheckCircle className="w-4 h-4" />;
    if (status === "manager_review_pending")
      return <Pending className="w-4 h-4" />;
    return <Assessment className="w-4 h-4" />;
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-primary mb-1">
            Performance Management
          </h1>
          <p className="text-gray-600">
            Manage employee performance evaluations and reviews
          </p>
        </div>
        <button
          onClick={() => navigate("/performance/create")}
          className="btn-primary flex items-center gap-2 text-sm px-4 py-2"
        >
          <Add className="w-4 h-4" />
          New Evaluation
        </button>
      </motion.div>

      {/* Filters */}
      <div className="mb-4 flex gap-2 flex-wrap">
        {[
          { value: "all", label: "All" },
          { value: "draft", label: "Draft" },
          { value: "self_assessment_pending", label: "Self Assessment" },
          { value: "manager_review_pending", label: "Pending Review" },
          { value: "completed", label: "Completed" },
        ].map((item) => (
          <button
            key={item.value}
            onClick={() => setFilter(item.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
              filter === item.value
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Evaluations List */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : evaluations.length === 0 ? (
        <div className="card text-center py-12">
          <Assessment className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg mb-4">No evaluations found</p>
          <button
            onClick={() => navigate("/performance/create")}
            className="btn-primary"
          >
            Create First Evaluation
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {evaluations.map((evaluation, index) => (
            <motion.div
              key={evaluation._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card hover:shadow-soft-lg transition-all cursor-pointer"
              onClick={() => navigate(`/performance/${evaluation._id}`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-primary">
                      {evaluation.employee_id?.user_id?.name || "Employee"}
                    </h3>
                    <span
                      className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 ${getStatusBadge(
                        evaluation.status
                      )}`}
                    >
                      {getStatusIcon(evaluation.status)}
                      {evaluation.status
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>
                      Period:{" "}
                      {new Date(evaluation.period_start).toLocaleDateString()} -{" "}
                      {new Date(evaluation.period_end).toLocaleDateString()}
                    </span>
                    <span className="capitalize">
                      {evaluation.period_type} Evaluation
                    </span>
                    {evaluation.overall_score !== null && (
                      <span className="flex items-center gap-1 font-semibold text-primary">
                        <Star className="w-4 h-4" />
                        Score: {evaluation.overall_score}/100
                      </span>
                    )}
                  </div>
                  {evaluation.goals?.length > 0 && (
                    <div className="mt-3 text-sm text-gray-600">
                      {evaluation.goals.length} goal(s) assigned
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/performance/${evaluation._id}`);
                    }}
                    className="p-2 text-primary hover:bg-primary/10 rounded-xl transition-all"
                  >
                    <Visibility className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
